import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { equipmentTypesApi } from './service';
import type {
  EquipmentTypeCreateValues,
  EquipmentTypeUpdateValues,
} from './schema';
import type { EquipmentType } from './types';

/**
 * Query key factory — one place to change the cache layout.
 *
 *   equipmentTypesKeys.all           every cached equipment-types query
 *   equipmentTypesKeys.lists()       all list queries (future: filters)
 *   equipmentTypesKeys.detail(id)    a single equipment type
 */
export const equipmentTypesKeys = {
  all: ['equipment-types'] as const,
  lists: () => [...equipmentTypesKeys.all, 'list'] as const,
  detail: (id: string) => [...equipmentTypesKeys.all, 'detail', id] as const,
};

export function useEquipmentTypes() {
  return useQuery({
    queryKey: equipmentTypesKeys.lists(),
    queryFn: equipmentTypesApi.list,
  });
}

export function useEquipmentType(id: string | undefined) {
  return useQuery({
    queryKey: id ? equipmentTypesKeys.detail(id) : equipmentTypesKeys.all,
    queryFn: () => equipmentTypesApi.getById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Mutations — same plain-invalidation pattern as the Vessels module
 * (no optimistic updates yet; the offline path lands in F12).
 */
export function useCreateEquipmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EquipmentTypeCreateValues) =>
      equipmentTypesApi.create(input),
    onSuccess: (created: EquipmentType) => {
      qc.invalidateQueries({ queryKey: equipmentTypesKeys.lists() });
      qc.setQueryData(equipmentTypesKeys.detail(created.id), created);
    },
  });
}

export function useUpdateEquipmentType(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: EquipmentTypeUpdateValues) =>
      equipmentTypesApi.update(id, patch),
    onSuccess: (updated: EquipmentType) => {
      qc.invalidateQueries({ queryKey: equipmentTypesKeys.lists() });
      qc.setQueryData(equipmentTypesKeys.detail(id), updated);
    },
  });
}

export function useDeleteEquipmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => equipmentTypesApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: equipmentTypesKeys.lists() });
      qc.removeQueries({ queryKey: equipmentTypesKeys.detail(id) });
    },
  });
}
